import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, ExternalLink } from "lucide-react";
import {
  adminReplyToFeedback,
  fetchAdminFeedback,
  updateFeedbackStatus,
  type AdminFeedbackItem,
  type FeedbackStatus,
} from "../../api/feedback";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { FeedbackThread, ReplyComposer, submissionAsMessage } from "../../components/FeedbackThread";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";

function formatDate(value: string | null | undefined) {
  return value ? new Date(value).toLocaleString() : "—";
}

const TYPE_VARIANT: Record<string, "info" | "success" | "neutral" | "danger"> = {
  bug: "danger",
  feature: "info",
  data: "success",
  other: "neutral",
};

function DetailBlock({ label, obj }: { label: string; obj: Record<string, unknown> | null }) {
  const entries = Object.entries(obj ?? {}).filter(([, v]) => v !== null && v !== "" && v !== undefined);
  if (entries.length === 0) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
        {label}
      </div>
      <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 12px", margin: 0 }}>
        {entries.map(([k, v]) => (
          <React.Fragment key={k}>
            <dt style={{ fontSize: 12, color: "var(--text-muted)" }}>{k}</dt>
            <dd style={{ fontSize: 12.5, color: "var(--text-secondary)", margin: 0, wordBreak: "break-word" }}>
              {String(v)}
            </dd>
          </React.Fragment>
        ))}
      </dl>
    </div>
  );
}

function FeedbackRow({ item }: { item: AdminFeedbackItem }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (status: FeedbackStatus) => updateFeedbackStatus(item.id, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin/feedback"] }),
  });

  const archived = item.status === "archived";
  const replied = !!item.lastRepliedAt && !item.awaitingReply;

  // Reply delivery, computed server-side per request: a Strava-only account
  // that adds a real email later becomes emailable on the next reply.
  const composerDisabled =
    item.submitterReachable === "none"
      ? "Can't reply: this account has been deleted, so there is nobody to send it to."
      : undefined;
  const composerWarning =
    item.submitterReachable === "in_app"
      ? "This account has no real email address yet. The reply will only show in the app (and as a push notification)."
      : undefined;

  return (
    <div style={{ padding: "12px 0", borderBottom: "1px solid var(--border-subtle)", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Badge variant={TYPE_VARIANT[item.type] ?? "neutral"}>{item.type}</Badge>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)", flex: 1, minWidth: 200 }}>
          {item.title}
        </span>
        {item.awaitingReply && <Badge variant="gold">Awaiting reply</Badge>}
        {replied && <Badge variant="success">Replied</Badge>}
        {item.linearUrl ? (
          <a
            href={item.linearUrl}
            target="_blank"
            rel="noreferrer"
            style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12.5, color: "var(--text-accent)", textDecoration: "none" }}
          >
            Open in Linear <ExternalLink size={12} />
          </a>
        ) : (
          <span style={{ fontSize: 12, color: "var(--text-faint)" }}>Not synced</span>
        )}
        {archived && <Badge variant="neutral">Archived</Badge>}
        <Button
          variant="ghost"
          size="sm"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate(archived ? "new" : "archived")}
        >
          {mutation.isPending ? "…" : archived ? "Restore" : "Archive"}
        </Button>
      </div>

      <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: "8px 0 0", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
        {item.body}
      </p>

      <DetailBlock label="Details" obj={item.details} />
      <DetailBlock label="Context" obj={item.context} />

      {(item.photoUrls?.length ?? 0) > 0 && (
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          {item.photoUrls.map((url) => (
            <a key={url} href={url} target="_blank" rel="noreferrer">
              <img
                src={url}
                alt="Screenshot attachment"
                style={{
                  width: 88,
                  height: 88,
                  objectFit: "cover",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--border-default)",
                  display: "block",
                }}
              />
            </a>
          ))}
        </div>
      )}

      <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 8 }}>
        {(item.submitterName || item.submitterEmail) ?? "Account deleted"}
        {item.submitterEmail ? ` · ${item.submitterEmail}` : ""} · {formatDate(item.createdAt)}
      </div>

      {/* Thread: the submission is the first message, then both sides' replies. */}
      {item.replies.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <FeedbackThread
            viewerRole="admin"
            showDelivery
            messages={[
              submissionAsMessage({ id: item.id, body: item.body, createdAt: item.createdAt, submitterName: item.submitterName }),
              ...item.replies,
            ]}
          />
        </div>
      )}
      <ReplyComposer
        send={(body) => adminReplyToFeedback(item.id, body)}
        onSent={() => queryClient.invalidateQueries({ queryKey: ["admin/feedback"] })}
        disabledReason={composerDisabled}
        warning={composerWarning}
        placeholder={`Reply to ${item.submitterName?.split(" ")[0] || "the submitter"}`}
      />
    </div>
  );
}

// "Awaiting reply" is the default: unanswered new rows plus any thread where
// the submitter wrote after our last reply (archived ones included).
const STATUS_FILTERS: { label: string; value: string }[] = [
  { label: "Awaiting reply", value: "awaiting" },
  { label: "New", value: "new" },
  { label: "Archived", value: "archived" },
  { label: "All", value: "" },
];

const TYPE_FILTERS: { label: string; value: string }[] = [
  { label: "All types", value: "" },
  { label: "Bug", value: "bug" },
  { label: "Feature", value: "feature" },
  { label: "Data", value: "data" },
  { label: "Other", value: "other" },
];

export default function AdminFeedbackPage() {
  const { user } = useAuth();
  const [status, setStatus] = React.useState("awaiting");
  const [type, setType] = React.useState("");

  const feedbackQuery = useQuery({
    queryKey: ["admin/feedback", status, type],
    queryFn: () => fetchAdminFeedback({ status, type }),
    enabled: !!user?.isAdmin,
  });

  if (!user?.isAdmin) return <AccessDenied />;

  const items = feedbackQuery.data?.feedback ?? [];
  const awaitingCount = items.filter((i) => i.awaitingReply).length;

  const selectStyle: React.CSSProperties = {
    height: "var(--control-sm)",
    padding: "0 10px",
    background: "var(--surface-sunken)",
    border: "1px solid var(--border-default)",
    borderRadius: "var(--radius-md)",
    color: "var(--text-primary)",
    fontSize: 13,
    fontFamily: "var(--font-sans)",
    outline: "none",
  };

  return (
    <AdminShell
      title="Feedback"
      action={
        awaitingCount > 0 ? (
          <Badge variant="info" icon={<MessageSquare size={11} />}>{awaitingCount} awaiting reply</Badge>
        ) : undefined
      }
    >
      <div style={{ display: "flex", gap: 10 }}>
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={selectStyle}>
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} style={selectStyle}>
          {TYPE_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>

      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-sm), var(--ring-top)",
          padding: "6px 18px 12px",
        }}
      >
        {feedbackQuery.isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 0" }}>
            <Skel height={40} />
            <Skel height={40} />
            <Skel height={40} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<MessageSquare size={20} />}
            title={status === "awaiting" ? "Nothing waiting on you" : "No feedback here"}
            hint={
              status === "awaiting"
                ? "Every thread has been answered. New submissions and user replies land here."
                : "Submissions from the app will show up in this inbox."
            }
            style={{ margin: "12px 0" }}
          />
        ) : (
          items.map((item) => <FeedbackRow key={item.id} item={item} />)
        )}
      </div>
    </AdminShell>
  );
}
